import { reactive } from 'vue'

import axios from 'axios'
import * as _ from 'lodash-es'
import moment from 'moment'

import bookingService from '../../booking/service/bookingService.js'
import loginService from '../../login/service/loginService.js'
import jobService from '../../job/service/jobService.js'
import sliderService from '../../common/service/sliderService.js'

/*
 * The resource list and the "bookings to complete" list are `Vue.observable` (step 41 of #614).
 * The menu watches the incomplete-bookings count; it used to do so through `$rootScope.$watch`,
 * which only ran inside a digest and needed `notifyAngular()` after every async load. Vue
 * reactivity removes both.
 */
const state = reactive({ resources: [], incompleteBookings: [] })

function loadIncompleteBookings() {
  return loginService
    .currentSession()
    .then(userData => resourcesService.findAssignedResources(userData))
    .then(assignedResources => jobService.findIncompleteBookings(assignedResources))
    .then(result => {
      state.incompleteBookings = (result.data && result.data.bookings) || []
      return state.incompleteBookings
    })
    .catch(err => {
      // A session reboot is routine. Anything else is not, and must not be swallowed:
      // this chain used to drop its nested rejections on the floor, which hid a 500 from
      // /api/job/incomplete for years. The prompt simply stopped appearing, and from the
      // outside that is indistinguishable from having nothing to complete.
      if (!(err && err.error && err.error.message === 'No session')) {
        console.log('Unable to load incomplete bookings', err)
      }
      state.incompleteBookings = []
      return state.incompleteBookings
    })
}

// Deliberately not opened for the user any more. It used to seize the screen on login,
// which was disruptive enough that the feature was effectively switched off; the count is
// offered in the menu instead, and opening it is the user's decision.
//
// Called from the entry (main.js), not from here. As a module this ran the moment it was
// imported - before Amplify had restored the token store - and `loginService.currentSession()`'s
// retry window could expire first, leaving the count at zero with nothing to retry it.
function start() {
  return loadIncompleteBookings()
}

var resourcesService = {
  start: start,

  findResourceById: function (resourceId) {
    return new Promise(function (resolve) {
      var resource = _.find(state.resources, ['_id', resourceId])
      resolve(resource)
    })
  },

  update: function () {
    return axios.get('/api/resource').then(function (result) {
      // `|| []` because the ternary this replaces only guarded a missing `data`: a response with
      // a `data` that carried no `resources` left it undefined, and every caller treats it as a
      // list.
      state.resources = (result.data && result.data.resources) || []
      return state.resources
    })
  },

  getAll: function () {
    return state.resources
  },

  hasConflicts: function (resourceId, date) {
    var bookings = bookingService.getBookingsForResources()
    date = moment(date)
    var startOfDay = moment(date.startOf('day'))
    var endOfDay = moment(date.endOf('day'))

    var conflicting = !!_.find(bookings[resourceId], function (firstBooking) {
      var bookingStart = moment(firstBooking.startsAt, 'DD-MM-YYYY HH:mm')
      var bookingEnd = moment(firstBooking.endsAt, 'DD-MM-YYYY HH:mm')

      var isSelectedDay =
        bookingStart.isBetween(startOfDay, endOfDay, null, '()') ||
        bookingEnd.isBetween(startOfDay, endOfDay, null, '()')
      if (!isSelectedDay) {
        return
      }

      return _.find(bookings[resourceId], function (secondBooking) {
        var isSameBooking = firstBooking == secondBooking
        if (isSameBooking) {
          return false
        }

        // The format matters: the bookings carry DD-MM-YYYY, and bare `moment()` reads that as
        // MM-DD-YYYY. Without it a booking on the 8th of October parsed as the 10th of August,
        // so the two dates were never both on the selected day and this could not report a
        // conflict. The first booking above has always passed the format; this one did not.
        var existingStart = moment(secondBooking.startsAt, 'DD-MM-YYYY HH:mm')
        var existingEnd = moment(secondBooking.endsAt, 'DD-MM-YYYY HH:mm')

        var isSelectedDay =
          existingStart.isBetween(startOfDay, endOfDay, null, '()') ||
          existingEnd.isBetween(startOfDay, endOfDay, null, '()')

        if (!isSelectedDay) {
          return false
        }

        return (
          bookingStart.isBetween(existingStart, existingEnd, null, '()') ||
          bookingEnd.isBetween(existingStart, existingEnd, null, '()') ||
          existingStart.isBetween(bookingStart, bookingEnd, null, '()') ||
          existingEnd.isBetween(bookingStart, bookingEnd, null, '()')
        )
      })
    })

    return conflicting
  },

  getIncompleteBookings: function () {
    return state.incompleteBookings
  },

  openIncompleteBookings: function () {
    if (!state.incompleteBookings.length) {
      return Promise.resolve()
    }

    return sliderService
      .openSlider('IncompleteBookingsCtrl', 'incompleteBookings.html', {
        bookings: state.incompleteBookings,
      })
      .then(() => loadIncompleteBookings())
      .catch(() => {
        // dismissed rather than saved: re-read in case anything changed meanwhile
        return loadIncompleteBookings()
      })
  },

  findAssignedResources: function (userData) {
    return resourcesService.update().then(() => {
      const assigned = _.filter(resourcesService.getAll(), resource => {
        // v6 sessions carry their tokens under `tokens`.
        return _.find(resource.users, ['userId', userData.tokens?.idToken?.payload?.['cognito:username']])
      })
      return assigned
    })
  },
}

export default resourcesService
